import { Controller } from "@hotwired/stimulus";

// Lists user's presets with rename/delete actions. Lives in a second-
// level offcanvas inside _manage_panel.html.erb. Opens via a "Manage
// saved presets →" link in the main drawer's preset block.
//
// Safe-DOM construction throughout — no innerHTML writes — to match
// the project's XSS hygiene convention (see filter_presets_controller.js
// _renderPicker for the same pattern).
export default class extends Controller {
  static targets = ["list"];
  static values = { endpoint: String };

  connect() {
    this._csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    this._loadSeq = 0;

    // Reload the list each time the offcanvas is shown so renames/deletes
    // performed in another tab (or stale caches) don't linger. Bound to a
    // stable property so disconnect() can unwire it cleanly — Stimulus can
    // re-connect controllers when their element is teleported, and the
    // Bootstrap offcanvas instance can outlive a single disconnect cycle.
    this._onShown = () => this._loadList();
    this.element.addEventListener("shown.bs.offcanvas", this._onShown);
  }

  disconnect() {
    if (this._onShown) {
      this.element.removeEventListener("shown.bs.offcanvas", this._onShown);
      this._onShown = null;
    }
  }

  // ─── List + load ──────────────────────────────────────────────────────────
  async _loadList() {
    const reqId = ++this._loadSeq;
    try {
      const res = await fetch(this.endpointValue, {
        credentials: "same-origin", headers: { Accept: "application/json" }
      });
      if (!res.ok) return;
      const presets = await res.json();
      // Offcanvas can re-open mid-fetch; drop stale responses.
      if (reqId !== this._loadSeq) return;
      this._render(presets);
    } catch { /* soft fail */ }
  }

  _render(presets) {
    // Defer if user is mid-rename; we'll re-render when their edit resolves.
    // Preserves in-flight typing if a sibling action (e.g. deleting another
    // row) triggers a re-render. Overwrite, not append — the freshest snapshot
    // wins once the edit completes.
    if (this.listTarget.querySelector("input")) {
      this._pendingRender = presets;
      return;
    }

    const list = this.listTarget;
    // Clear via safe DOM removal (no innerHTML) per project XSS hygiene.
    while (list.firstChild) list.removeChild(list.firstChild);

    if (!presets.length) {
      const empty = document.createElement("p");
      empty.className = "text-muted";
      empty.textContent = "No saved presets yet.";
      list.appendChild(empty);
      return;
    }

    presets.forEach((p) => {
      const row = document.createElement("div");
      row.className = "d-flex align-items-center justify-content-between gap-2 py-2";
      row.dataset.presetId = p.id;

      const name = document.createElement("span");
      name.className = "flex-grow-1 filter-manage-presets__name";
      name.textContent = p.name;

      const renameBtn = document.createElement("button");
      renameBtn.type = "button";
      renameBtn.className = "btn btn-sm filter-manage-presets__rename";
      renameBtn.dataset.action = "filter-manage-presets#startRename";
      renameBtn.textContent = "Rename";

      const deleteBtn = document.createElement("button");
      deleteBtn.type = "button";
      deleteBtn.className = "btn btn-sm btn-outline-danger";
      deleteBtn.dataset.action = "filter-manage-presets#delete";
      deleteBtn.textContent = "Delete";

      row.appendChild(name);
      row.appendChild(renameBtn);
      row.appendChild(deleteBtn);
      list.appendChild(row);
    });
  }

  _flushPending() {
    if (this._pendingRender !== undefined) {
      const pending = this._pendingRender;
      this._pendingRender = undefined;
      this._render(pending);
    }
  }

  _restoreNameSpan(input, original) {
    const row = input.closest("[data-preset-id]");
    const restored = document.createElement("span");
    restored.className = "flex-grow-1 filter-manage-presets__name";
    restored.textContent = original;
    input.replaceWith(restored);
    if (row) this._restoreRowChrome(row);
    this._flushPending();
  }

  // Undo the startRename chrome swap: Rename visible again, Save/Cancel gone.
  _restoreRowChrome(row) {
    const renameBtn = row.querySelector(".filter-manage-presets__rename");
    if (renameBtn) {
      renameBtn.hidden = false;
      renameBtn.style.display = "";
    }
    row.querySelectorAll(".btn-primary, .btn-link").forEach((btn) => btn.remove());
  }

  // ─── Rename ───────────────────────────────────────────────────────────────
  startRename(event) {
    const row = event.currentTarget.closest("[data-preset-id]");
    if (!row) return;
    const span = row.querySelector(".filter-manage-presets__name");
    if (!span) return;
    const original = span.textContent;

    const input = document.createElement("input");
    input.type = "text";
    input.className = "form-control form-control-sm";
    input.value = original;
    span.replaceWith(input);
    input.focus();
    input.select();

    // Swap the row's action chrome for the edit: hide Rename and add
    // Save/Cancel next to Delete, so the edit has a visible commit path
    // (Enter alone was the LRPO-5507 gap on "Save current as preset").
    const renameBtn = row.querySelector(".filter-manage-presets__rename");
    if (renameBtn) {
      renameBtn.hidden = true;
      renameBtn.style.display = "none";
    }

    const saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "btn btn-sm btn-primary";
    saveBtn.textContent = "Save";
    saveBtn.addEventListener("click", async () => {
      await this._submitRename(row.dataset.presetId, input.value.trim(), original, input);
    });

    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn btn-sm btn-link";
    cancelBtn.textContent = "Cancel";
    cancelBtn.addEventListener("click", () => {
      this._restoreNameSpan(input, original);
    });

    const deleteBtn = row.querySelector('button[data-action="filter-manage-presets#delete"]');
    if (deleteBtn) deleteBtn.before(saveBtn, cancelBtn);

    // Per-invocation closure: each startRename creates a fresh input, so the
    // listener is GC'd with the input on Escape or re-render. No leak risk.
    input.addEventListener("keydown", async (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        await this._submitRename(row.dataset.presetId, input.value.trim(), original, input);
      } else if (e.key === "Escape") {
        this._restoreNameSpan(input, original);
      }
    });
  }

  async _submitRename(id, name, original, input) {
    if (!name || name === original) {
      this._restoreNameSpan(input, original);
      return;
    }
    if (!this._csrf) { alert("Session expired. Please reload the page."); return; }
    try {
      const res = await fetch(`${this.endpointValue}/${id}`, {
        method: "PATCH", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf, Accept: "application/json" },
        body: JSON.stringify({ lead_filter_preset: { name } })
      });
      if (res.ok) {
        // Replace the input with the new name BEFORE reloading so _render's
        // in-flight-edit guard sees a clean list and doesn't defer this snapshot.
        this._restoreNameSpan(input, name);
        this._loadList();
      } else if (res.status === 422) {
        const body = await res.json();
        alert(body.errors?.name?.[0] || "Rename failed");
      } else {
        alert("Rename failed");
      }
    } catch { alert("Network error while renaming."); }
  }

  // ─── Delete ───────────────────────────────────────────────────────────────
  async delete(event) {
    const row = event.currentTarget.closest("[data-preset-id]");
    if (!row) return;
    const nameEl = row.querySelector(".filter-manage-presets__name");
    const name = nameEl ? nameEl.textContent : "this preset";
    if (!confirm(`Delete preset "${name}"? This cannot be undone.`)) return;
    if (!this._csrf) { alert("Session expired. Please reload the page."); return; }
    try {
      const res = await fetch(`${this.endpointValue}/${row.dataset.presetId}`, {
        method: "DELETE", credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrf, Accept: "application/json" }
      });
      if (res.ok || res.status === 204) this._loadList();
    } catch { /* soft fail */ }
  }
}
